import {
  Add,
  AddPhotoAlternate,
  ArrowRight,
  Cancel,
  ChevronLeft,
  Delete,
  Edit,
  Search
} from '@mui/icons-material'
import {
  Box,
  Button,
  Divider,
  Icon,
  IconButton,
  Modal,
  Paper,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import React, { useRef } from 'react'
import { connect } from 'react-redux'
import NivosRTE from 'components/nivos-rte'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as NivosPostOperations,
  operations as nivosPostOperations
} from 'states/dux/nivos-post/operations'
import { Payload as NivosPostPayload } from 'states/dux/nivos-post/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import NivosPost from 'types/nivos-post'
import {
  mapOperationsToDispatchProps,
  objectifyDateStringFields,
  post
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  nivosPost: NivosPostPayload
  user: UserPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    nivosPost: NivosPostOperations
  }
}

type AppProps = StateProps & DispatchProps

const PostControlsFC: React.FC<any> = (props: AppProps) => {
  const { layout, theme, nivosPost, operations, user } = props
  const isMobileView = useMediaQuery('(max-width:600px)')
  const [modal, setModal] = React.useState({
    create: false,
    edit: false,
    delete: false
  })

  const CreateEditPost: React.FC<{ initPost?: NivosPost }> = props => {
    const { initPost } = props

    const defaultErrorState = {
      title: false,
      content: false
    }

    const defaultInputWidth = 200

    const [addHovered, setHovered] = React.useState(false)
    const [content, setContent] = React.useState('')
    const [contentRte, setContentRte] = React.useState('')
    const [coverImage, setCoverImage] = React.useState<FileList>()
    const [errors, setErrors] = React.useState(defaultErrorState)
    const [formLoading, setFormLoading] = React.useState(false)
    const [title, setTitle] = React.useState('')
    const [inputWidth, setInputWidth] = React.useState(defaultInputWidth)
    const imageInputRef = useRef<HTMLInputElement>(null)

    const generateFormData = (): FormData => {
      const formData = new FormData()
      formData.append('title', title)
      formData.append('content', content)
      formData.append('content_rte', contentRte)
      formData.append('created_by', user.id.toString())
      formData.append('updated_by', user.id.toString())

      if (coverImage !== undefined) {
        for (let i = 0; i < coverImage.length; i++) {
          formData.append('post_images', coverImage[i], coverImage[i].name)
        }
      }

      return formData
    }

    const clearForm = (): void => {
      if (title.trim() !== '') {
        setTitle('Enter title here...')
      }
      setContent('')
      setContentRte('')
      setCoverImage(undefined)
    }

    const validateInputs = (): void => {
      let errorState = { ...errors }
      let hasError = false
      const requiredInputs = [{ label: 'content', val: content }]

      setFormLoading(true)

      // Validate required fields
      requiredInputs.forEach(input => {
        if (input.val === null || input.val === '' || input.val === undefined) {
          errorState = { ...errorState, [input.label]: true }
          hasError = true
        } else {
          errorState = { ...errorState, [input.label]: false }
        }
      })

      setErrors({ ...errorState })

      if (hasError) {
        setFormLoading(false)
        return
      }

      if (initPost === undefined) {
        void post('/post/', generateFormData(), true).then(data => {
          operations.nivosPost.add(objectifyDateStringFields(data))
          clearForm()
          setFormLoading(false)
          operations.nivosPost.loading(false)
          setModal({ ...modal, create: false })
        })
      } else {
        void post(
          `/post/${initPost.id}/`,
          generateFormData(),
          true,
          false,
          'PATCH'
        ).then(data => {
          operations.nivosPost.update(objectifyDateStringFields(data))
          clearForm()
          setFormLoading(false)
          operations.nivosPost.loading(false)
          setModal({ ...modal, create: false })
        })
      }
    }

    React.useEffect(() => {
      if (initPost !== undefined) {
        setTitle(initPost.title)
        setContent(initPost.content)
        setContentRte(initPost.contentRte)
      }
    }, [initPost])

    React.useEffect(() => {
      if (title.length * 13 > defaultInputWidth) {
        setInputWidth((title.length + 1) * 13)
      } else {
        setInputWidth(defaultInputWidth)
      }
    }, [title])

    return (
      <Box>
        <Box
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          paddingY={4}
        >
          <TextField
            variant={'standard'}
            sx={{
              backgroundColor: theme.primaryColor,
              color: theme.secondaryColor,
              width: `${inputWidth}px`,
              minWidth: '200px',
              margin: 'auto'
            }}
            InputProps={{
              sx: { fontSize: '28px' },
              inputProps: { style: { textAlign: 'center' } }
            }}
            defaultValue={
              initPost !== undefined ? initPost.title : 'Enter title...'
            }
            onChange={event => setTitle(event.currentTarget.value)}
          />
        </Box>
        <input
          ref={imageInputRef}
          type={'file'}
          accept={'image/*'}
          style={{ display: 'none' }}
          onChange={event => {
            if (event.currentTarget.files != null) {
              setCoverImage(event.currentTarget.files)
            }
          }}
        />
        {coverImage !== undefined && (
          <Tooltip title={'Change cover image'}>
            <img
              src={URL.createObjectURL(coverImage[0])}
              width={'100%'}
              height={200}
              alt={'selected-nivos-post-cover-image'}
              style={{
                objectFit: 'cover',
                borderRadius: 8,
                cursor: 'pointer'
              }}
              onClick={() => {
                if (imageInputRef?.current != null) {
                  imageInputRef.current.showPicker()
                }
              }}
            />
          </Tooltip>
        )}
        {coverImage === undefined &&
          initPost !== undefined &&
          initPost.images !== undefined &&
          initPost.images.length > 0 && (
            <Tooltip title={'Change cover image'}>
              <img
                src={initPost.images[0].url}
                width={'100%'}
                height={200}
                alt={'selected-nivos-post-cover-image'}
                style={{
                  objectFit: 'cover',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
                onClick={() => {
                  if (imageInputRef?.current != null) {
                    imageInputRef.current.showPicker()
                  }
                }}
              />
            </Tooltip>
        )}
        {coverImage === undefined &&
          (initPost === undefined || initPost.images?.length === 0) && (
            <Box width={'fit-content'} marginX={'auto'}>
              <Tooltip placement={'top'} title={'Add cover photo'}>
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  height={150}
                  width={isMobileView ? '75vw' : 700}
                  onMouseEnter={() => setHovered(true)}
                  onMouseLeave={() => setHovered(false)}
                  onClick={() => {
                    if (imageInputRef?.current != null) {
                      imageInputRef.current.showPicker()
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    transition: '0.5s',
                    fontSize: addHovered ? 64 : 48
                  }}
                >
                  <Box display={'flex'} width={1}>
                    <Box
                      width={45}
                      height={45}
                      borderLeft={5}
                      borderTop={5}
                      borderRadius={'4px'}
                      borderColor={theme.secondaryColor}
                      sx={{ transition: '0.5s' }}
                    />
                    <Box flexGrow={1} />
                    <Box
                      width={45}
                      height={45}
                      borderRight={5}
                      borderTop={5}
                      borderRadius={'4px'}
                      borderColor={theme.secondaryColor}
                      sx={{ transition: '0.5s' }}
                    />
                  </Box>
                  <Box
                    flexGrow={1}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                  >
                    <Icon fontSize={'inherit'}>
                      <AddPhotoAlternate fontSize={'inherit'} />
                    </Icon>
                  </Box>
                  <Box display={'flex'} width={0.99}>
                    <Box
                      width={45}
                      height={45}
                      borderLeft={5}
                      borderBottom={5}
                      borderRadius={'4px'}
                      borderColor={theme.secondaryColor}
                      sx={{ transition: '0.5s' }}
                    />
                    <Box flexGrow={1} />
                    <Box
                      width={45}
                      height={45}
                      borderRight={5}
                      borderBottom={5}
                      borderRadius={'4px'}
                      borderColor={theme.secondaryColor}
                      sx={{ transition: '0.5s' }}
                    />
                  </Box>
                </Box>
              </Tooltip>
            </Box>
        )}
        <Box padding={2} paddingY={5}>
          <NivosRTE
            rteState={contentRte}
            setRteState={setContentRte}
            setPLainState={setContent}
          />
        </Box>
        <Box display={'flex'} padding={2} justifyContent={'flex-end'}>
          <Button
            variant={'contained'}
            disabled={formLoading}
            onClick={() => validateInputs()}
          >
            {formLoading ? 'Loading...' : 'Submit'}
          </Button>
        </Box>
      </Box>
    )
  }

  const CreatePostModal: React.FC = () => {
    return (
      <Modal
        open={modal.create}
        onClose={() => setModal({ ...modal, create: false })}
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={'center'}
          height={1}
        >
          <Paper
            variant={'elevation'}
            sx={{
              width: isMobileView ? '97vw' : 800,
              minHeight: '50vh',
              maxHeight: isMobileView ? '100vh' : '90vh',
              overflow: 'auto',
              padding: 2,
              borderRadius: '6px'
            }}
            elevation={8}
          >
            <Box
              display={'flex'}
              justifyContent={'flex-start'}
              alignItems={'center'}
              padding={2}
            >
              <Box padding={1} flexGrow={1}>
                <Typography variant={isMobileView ? 'h4' : 'h3'}>
                  Create new post
                </Typography>
              </Box>
              <Box display={'flex'} alignItems={'center'}>
                <Tooltip placement={'top'} title={'Cancel'}>
                  <IconButton
                    onClick={() => setModal({ ...modal, create: false })}
                  >
                    <Cancel />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Divider />
            <CreateEditPost />
          </Paper>
        </Box>
      </Modal>
    )
  }

  const EditPostModal: React.FC = () => {
    const [selectedPost, setSelectedPost] = React.useState<NivosPost>()

    const PostItem: React.FC<{ post: NivosPost }> = props => {
      const { post } = props
      const [itemHovered, setItemHovered] = React.useState(false)
      return (
        <Box
          display={'flex'}
          alignItems={'center'}
          borderBottom={1}
          onClick={() => setSelectedPost(post)}
          onMouseEnter={() => setItemHovered(true)}
          onMouseLeave={() => setItemHovered(false)}
          sx={{
            cursor: 'pointer'
          }}
        >
          <Box paddingLeft={2}>
            <Typography variant={'h5'}>{post.createdAt.getDate()}</Typography>
            <Typography variant={'h5'}>
              {post.createdAt.toLocaleString('default', {
                month: 'short'
              })}
            </Typography>
          </Box>
          <Box
            paddingLeft={2}
            borderRight={1}
            height={'45px'}
            color={theme.secondaryColor}
          />
          <Box
            paddingRight={2}
            borderLeft={1}
            height={'45px'}
            color={theme.secondaryColor}
          />
          <Box>
            <Typography variant={'h5'}>{post.title}</Typography>
          </Box>
          <Box
            padding={1}
            paddingLeft={itemHovered ? 2 : 1}
            sx={{ transition: '0.5s' }}
          >
            <Icon>
              <ArrowRight />
            </Icon>
          </Box>
        </Box>
      )
    }

    return (
      <Modal
        open={modal.edit}
        onClose={() => setModal({ ...modal, edit: false })}
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={'center'}
          height={1}
        >
          <Paper
            variant={'elevation'}
            sx={{
              width: isMobileView
                ? '97vw'
                : selectedPost !== undefined
                  ? 800
                  : 600,
              height: isMobileView ? '100vh' : 'initial',
              minHeight: 400,
              maxHeight: isMobileView ? '100vh' : '90vh',
              overflow: 'auto',
              padding: 2,
              borderRadius: '6px'
            }}
            elevation={8}
          >
            <Box
              display={'flex'}
              justifyContent={'flex-start'}
              alignItems={'center'}
              padding={2}
            >
              <Box padding={1} flexGrow={1}>
                <Typography variant={'h4'}>Edit Post</Typography>
              </Box>
              <Box display={'flex'} alignItems={'center'}>
                {selectedPost !== undefined && (
                  <Tooltip placement={'top'} title={'Return to selection'}>
                    <IconButton onClick={() => setSelectedPost(undefined)}>
                      <ChevronLeft />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip placement={'top'} title={'Cancel'}>
                  <IconButton
                    onClick={() => setModal({ ...modal, edit: false })}
                  >
                    <Cancel />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Divider />
            {selectedPost === undefined && (
              <Box display={'flex'} flexDirection={'column'} padding={1}>
                {nivosPost.data.map((post, index) => (
                  <PostItem key={index} post={post} />
                ))}
              </Box>
            )}
            {selectedPost !== undefined && (
              <CreateEditPost initPost={selectedPost} />
            )}
          </Paper>
        </Box>
      </Modal>
    )
  }

  const DeletePostModal: React.FC = () => {
    const [selectedPost, setSelectedPost] = React.useState<NivosPost>()
    const PostItem: React.FC<{ post: NivosPost }> = props => {
      const { post } = props
      const [itemHovered, setItemHovered] = React.useState(false)
      return (
        <Box
          display={'flex'}
          alignItems={'center'}
          borderBottom={1}
          onClick={() => setSelectedPost(post)}
          onMouseEnter={() => setItemHovered(true)}
          onMouseLeave={() => setItemHovered(false)}
          sx={{
            cursor: 'pointer'
          }}
        >
          <Box paddingLeft={2}>
            <Typography variant={'h5'}>{post.createdAt.getDate()}</Typography>
            <Typography variant={'h5'}>
              {post.createdAt.toLocaleString('default', {
                month: 'short'
              })}
            </Typography>
          </Box>
          <Box
            paddingLeft={2}
            borderRight={1}
            height={'45px'}
            color={theme.secondaryColor}
          />
          <Box
            paddingRight={2}
            borderLeft={1}
            height={'45px'}
            color={theme.secondaryColor}
          />
          <Box>
            <Typography variant={'h5'}>{post.title}</Typography>
          </Box>
          <Box
            padding={1}
            paddingLeft={itemHovered ? 2 : 1}
            sx={{ transition: '0.5s' }}
          >
            <Icon>
              <ArrowRight />
            </Icon>
          </Box>
        </Box>
      )
    }

    return (
      <Modal
        open={modal.delete}
        onClose={() => setModal({ ...modal, delete: false })}
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={'center'}
          height={1}
        >
          <Modal open={selectedPost !== undefined}>
            <Box
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'center'}
              alignItems={'center'}
              height={1}
            >
              <Paper
                variant={'elevation'}
                sx={{
                  width: 'min-content',
                  maxHeight: '95vh',
                  overflow: 'auto',
                  padding: 2
                }}
                elevation={8}
              >
                <Typography gutterBottom>Delete this Post?</Typography>
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'space-evenly'}
                  width={300}
                >
                  <Button
                    variant={'contained'}
                    onClick={() => {
                      if (selectedPost !== undefined) {
                        void post(
                          `/post/${selectedPost.id}/`,
                          {},
                          false,
                          false,
                          'DELETE'
                        )
                        operations.nivosPost.deletePost(selectedPost)
                        setSelectedPost(undefined)
                        setModal({ ...modal, delete: false })
                      }
                    }}
                  >
                    Delete
                  </Button>
                  <Button
                    variant={'outlined'}
                    onClick={() => setSelectedPost(undefined)}
                  >
                    Cancel
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Modal>
          <Paper
            variant={'elevation'}
            sx={{
              width: isMobileView
                ? '98vw'
                : selectedPost !== undefined
                  ? 800
                  : 600,
              height: isMobileView ? '100vh' : 'initial',
              minHeight: 400,
              maxHeight: isMobileView ? '100vh' : '90vh',
              overflow: 'auto',
              padding: 2,
              borderRadius: '6px'
            }}
            elevation={8}
          >
            <Box
              display={'flex'}
              justifyContent={'flex-start'}
              alignItems={'center'}
              padding={2}
            >
              <Box padding={1} flexGrow={1}>
                <Typography variant={'h4'}>Delete Post</Typography>
              </Box>
              <Box display={'flex'} alignItems={'center'}>
                {selectedPost !== undefined && (
                  <Tooltip placement={'top'} title={'Return to selection'}>
                    <IconButton onClick={() => setSelectedPost(undefined)}>
                      <ChevronLeft />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip placement={'top'} title={'Cancel'}>
                  <IconButton
                    onClick={() => setModal({ ...modal, delete: false })}
                  >
                    <Cancel />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Divider />
            {selectedPost === undefined && (
              <Box display={'flex'} flexDirection={'column'} padding={1}>
                {nivosPost.data.map((post, index) => (
                  <PostItem key={index} post={post} />
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      </Modal>
    )
  }

  return (
    <Box
      display={'flex'}
      justifyContent={'flex-end'}
      alignItems={'center'}
      paddingTop={1}
    >
      <CreatePostModal />
      <EditPostModal />
      <DeletePostModal />
      <Box paddingLeft={1}>
        <Tooltip title={'Create a post'}>
          <IconButton onClick={() => setModal({ ...modal, create: true })}>
            <Add />
          </IconButton>
        </Tooltip>
      </Box>
      <Box paddingLeft={1}>
        <Tooltip title={'Edit your post'}>
          <IconButton onClick={() => setModal({ ...modal, edit: true })}>
            <Edit />
          </IconButton>
        </Tooltip>
      </Box>
      <Box paddingLeft={1}>
        <Tooltip title={'Delete your post'}>
          <IconButton onClick={() => setModal({ ...modal, delete: true })}>
            <Delete />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        paddingLeft={1}
        borderRight={1}
        height={'56px'}
        color={theme.secondaryColor}
      />
      <Box
        paddingRight={1}
        borderLeft={1}
        height={'56px'}
        color={theme.secondaryColor}
      />
      <TextField
        InputProps={{
          startAdornment: <Search />
        }}
        type={'text'}
        placeholder={`${layout.language.translations.search}...`}
      />
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    nivosPost: state.nivosPost,
    user: state.user
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  nivosPost: nivosPostOperations
})

const PostControls = connect(
  mapStateToProps,
  mapDispatchToProps
)(PostControlsFC) as React.FC

export default PostControls
